import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  API,
  STUDIO as DEFAULT_STUDIO,
  MAGNETS as DEFAULT_MAGNETS,
  MASSAGES as DEFAULT_MASSAGES,
  FOURLIFE_PRODUCTS as DEFAULT_FOURLIFE,
  FOURLIFE_HIGHLIGHTS as DEFAULT_HIGHLIGHTS,
  FOREVER_PILLARS as DEFAULT_FOREVER_PILLARS,
  FOREVER_PACKAGES as DEFAULT_FOREVER_PACKAGES,
  BLOG_ARTICLES as DEFAULT_ARTICLES,
  BLOG_VIDEOS as DEFAULT_VIDEOS,
  TESTIMONIALS as DEFAULT_TESTIMONIALS,
} from "@/constants/content";

export const DEFAULTS = {
  studio: DEFAULT_STUDIO,
  magnets: DEFAULT_MAGNETS,
  massages: DEFAULT_MASSAGES,
  fourlife: DEFAULT_FOURLIFE,
  highlights: DEFAULT_HIGHLIGHTS,
  foreverPillars: DEFAULT_FOREVER_PILLARS,
  foreverPackages: DEFAULT_FOREVER_PACKAGES,
  articles: DEFAULT_ARTICLES,
  videos: DEFAULT_VIDEOS,
  testimonials: DEFAULT_TESTIMONIALS,
};

const ContentContext = createContext({ content: DEFAULTS, loaded: false, refresh: () => {} });

function mergeWithDefaults(raw) {
  if (!raw || typeof raw !== "object") return DEFAULTS;
  return {
    studio: raw.studio && typeof raw.studio === "object" ? { ...DEFAULTS.studio, ...raw.studio } : DEFAULTS.studio,
    magnets: Array.isArray(raw.magnets) && raw.magnets.length ? raw.magnets : DEFAULTS.magnets,
    massages: Array.isArray(raw.massages) && raw.massages.length ? raw.massages : DEFAULTS.massages,
    fourlife: Array.isArray(raw.fourlife) && raw.fourlife.length ? raw.fourlife : DEFAULTS.fourlife,
    highlights: Array.isArray(raw.highlights) && raw.highlights.length ? raw.highlights : DEFAULTS.highlights,
    foreverPillars: Array.isArray(raw.foreverPillars) && raw.foreverPillars.length ? raw.foreverPillars : DEFAULTS.foreverPillars,
    foreverPackages: Array.isArray(raw.foreverPackages) && raw.foreverPackages.length ? raw.foreverPackages : DEFAULTS.foreverPackages,
    articles: Array.isArray(raw.articles) && raw.articles.length ? raw.articles : DEFAULTS.articles,
    videos: Array.isArray(raw.videos) && raw.videos.length ? raw.videos : DEFAULTS.videos,
    testimonials: Array.isArray(raw.testimonials) ? raw.testimonials : DEFAULTS.testimonials,
  };
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/content`);
      setContent(mergeWithDefaults(data));
    } catch {
      setContent(DEFAULTS);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <ContentContext.Provider value={{ content, loaded, refresh, setContent }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useSiteContent() {
  return useContext(ContentContext);
}
